import type { ActivityType as DbActivityType } from '@cortex/database';
import { ActivityFeed, type ActivityFeedItem } from '@cortex/ui';

import {
  getActivityMessage,
  mapDbActivityToUiType,
} from '@/lib/activity-mappers';

interface RawActivity {
  id: string;
  type: string;
  data: Record<string, unknown> | null;
  created_at: string;
  requests: {
    title: string;
    request_number: number;
  } | null;
}

interface DashboardActivityProps {
  activities: RawActivity[];
  isLoading: boolean;
  onActivityClick?: (requestNumber: number) => void;
  onRefresh?: () => void;
}

function transformActivities(raw: RawActivity[]): ActivityFeedItem[] {
  return raw.map((activity) => {
    const dbType = activity.type as DbActivityType;
    const data = activity.data ?? undefined;
    const requestTitle = activity.requests?.title ?? '';
    const message = getActivityMessage(dbType, data);

    return {
      id: activity.id,
      type: mapDbActivityToUiType(dbType),
      message: requestTitle ? `${message} — ${requestTitle}` : message,
      timestamp: new Date(activity.created_at),
    };
  });
}

export function DashboardActivity({
  activities,
  isLoading,
  onActivityClick,
  onRefresh,
}: DashboardActivityProps) {
  const feedItems = transformActivities(activities);

  const activityClickHandler = onActivityClick
    ? (item: ActivityFeedItem) => {
        const raw = activities.find((a) => a.id === item.id);
        if (raw?.requests?.request_number) {
          onActivityClick(raw.requests.request_number);
        }
      }
    : undefined;

  return (
    <ActivityFeed
      activities={feedItems}
      title="Recent Activity"
      size="md"
      loading={isLoading}
      emptyMessage="No recent activity"
      showTimeline
      {...(onRefresh != null && { onRefresh })}
      {...(activityClickHandler != null && {
        onActivityClick: activityClickHandler,
      })}
    />
  );
}
